import { Fragment, FunctionComponent, PropsWithChildren } from "react";
import { Transition, Dialog } from "@headlessui/react";

import ModalHeader from "./ModalHeader";
import { ModalContent } from "./ModalContent";
import ModalFooter from "./ModalFooter";
import { LineLoader } from "../Loaders";
import { ModalTitle } from "./ModalTitle";
import { ModalDescription } from "./ModalDescription";
import { fns } from "cy-util";
import { cn } from "../../utils/cn";

export interface ModalProps {
  className?: string;
  isOpen: boolean;
  onClose?: () => void;
  /**
   * Modal turns fullscreen on small displays
   */
  allowFullScreen?: boolean;
  hideBackground?: boolean;
  loading?: boolean;
  afterLeave?: () => void;
  "data-testid"?: string;
}

/**
 *
 */
const ModalRoot: FunctionComponent<PropsWithChildren<ModalProps>> = ({
  className,
  children,
  hideBackground,
  isOpen,
  onClose,
  loading,
  allowFullScreen,
  afterLeave,
  "data-testid": dataTestId,
}) => {
  onClose = onClose || fns.noop;
  const testId = dataTestId || "modal";

  return (
    <Transition
      appear
      show={isOpen || false}
      as={Fragment}
      afterLeave={afterLeave}
    >
      <Dialog
        as="div"
        className="relative z-50"
        onClose={onClose}
        data-testid={testId}
      >
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div
            className={cn(
              "fixed inset-0 bg-black",
              !hideBackground && "bg-opacity-25",
            )}
            data-testid="modal-overlay"
          />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div
            className={cn(
              allowFullScreen ? "sm:p-4" : "p-4",
              "flex h-0 min-h-full items-center justify-center text-center",
            )}
          >
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel
                className={cn(
                  allowFullScreen
                    ? "h-full w-full sm:h-fit sm:w-[70vw] sm:max-w-2xl sm:min-w-[20rem] sm:rounded-2xl"
                    : "max-w-2xl min-w-[20rem] rounded-2xl sm:w-[70vw]",
                  className,
                  "flex max-h-full transform flex-col overflow-hidden bg-white text-left align-middle shadow-xl transition-all",
                )}
                data-testid="modal-content"
              >
                {loading && (
                  <LineLoader
                    className="absolute top-0"
                    data-testid="modal-loading"
                  ></LineLoader>
                )}
                {children}
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export const Modal = Object.assign(ModalRoot, {
  Header: ModalHeader,
  Content: ModalContent,
  Footer: ModalFooter,
  Title: ModalTitle,
  Description: ModalDescription,
});
